import React, { useEffect, useMemo } from 'react';
import { useIsFetching, useMutation } from '@tanstack/react-query';
import useOrderPackage from '../../../hooks/useOrderPackage';
import useOption from '../../../hooks/useOption';
import useBackgrounds from '../../../hooks/useBackgrounds';
import usePoses from '../../../hooks/usePoses';
import { addonSheetToSheet } from '../MultiPose/functions';
import Builder from '../MultiPose/Builder';
import { AddonSheet } from '../../../models';
import useAddons from '../../../hooks/useAddons';
import { sheetUnitPrice } from '../../../utils/functions';

export default function SeniorIndividualPrints({ onSave, orderPackageId }) {
  // Data
  const { data: orderPackage } = useOrderPackage(orderPackageId);
  const { data: option } = useOption(orderPackage && orderPackage.optionId, orderPackage && orderPackage.schoolId);
  const { data: backgrounds } = useBackgrounds(orderPackage && orderPackage.packageId);
  const { data: poses } = usePoses(orderPackage && orderPackage.studentId, orderPackage && orderPackage.packageId);
  const { data: addons } = useAddons(false);

  const imageTypes = useMemo(() => {
    if (!orderPackage) return [];

    const yearbookPoseImageType = { name: 'Yearbook Pose', id: -1, count: 1 };

    const addonSheetsToImageType = orderPackage
      .addonSheets
      .map((addonSheet) => ({ name: addonSheet.addon.name, id: addonSheet.addon.id }));

    return [...addonSheetsToImageType, yearbookPoseImageType];
  }, [orderPackage?.addonSheets?.length]);

  const {
    mutate,
    isLoading,
  } = useMutation({
    mutationFn: ({ sheets }) => {
      // Destroy any sheets on the orderPackage (if any), and replace with the new sheets
      const oldSheets = orderPackage.addonSheets.map((sheet) => {
        sheet.isMarkedForDestruction = true;

        return sheet;
      });

      const yearbookSheet = sheets.find((sheet) => sheet.imageType.name === 'Yearbook Pose');
      const sheetsWithoutYearbookPose = sheets.filter((sheet) => sheet.imageType.name !== 'Yearbook Pose');

      const newSheets = sheetsWithoutYearbookPose.map((sheetParams) => {
        const addon = addons.find((cur) => cur.id.toString() === sheetParams.imageType.id.toString());

        return new AddonSheet({ ...sheetParams, addon, addonId: addon.id });
      });

      orderPackage.addonSheets = [...oldSheets, ...newSheets];
      orderPackage.seniorImageId = yearbookSheet.poseId;

      return orderPackage.save({ with: 'addonSheets' });
    },
    onSuccess: () => {
      if (Object.keys(orderPackage.errors).length > 0) return;

      onSave();
    },
  });

  // Calculate subtotal to present during review
  const subtotal = useMemo(() => {
    if (!orderPackage) return 0;
    if (!option) return 0;

    // sheetUnitPrice: server-stamped snapshot, shared fallback rule (keyed on
    // the ORDER PACKAGE price, like the charge — the old inline rule keyed on
    // the option price and could disagree with checkout).
    return option.prices[0].price + orderPackage.addonSheets.reduce((acc, cur) => (
      acc + sheetUnitPrice(cur, orderPackage)
    ), 0);
  }, [!!orderPackage, !!option]);

  // UI Hooks
  const isFetching = useIsFetching();

  useEffect(() => {
    document.querySelectorAll('.rails-nav').forEach((node) => { node.style.display = 'none'; });

    return () => document.querySelectorAll('.rails-nav').forEach((node) => { node.style.display = 'flex'; });
  }, []);

  if (!orderPackage) return null;
  if (isFetching) return null;

  return (
    <Builder
      backgrounds={backgrounds}
      imageTypes={imageTypes}
      initialSheets={orderPackage.addonSheets.map((addonSheet) => addonSheetToSheet(addonSheet))}
      isLoading={isLoading}
      option={{ ...option, poses: Number.POSITIVE_INFINITY, prices: [{ price: subtotal }] }}
      packageId={orderPackage.packageId}
      poses={poses}
      onConfirm={(sheets) => mutate({ sheets })}
    />
  );
}
